import { formatDate } from '@angular/common';
import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgModel } from '@angular/forms';
import { startWith } from 'rxjs';
import { shouldShowAstralValidationError } from '../astral-components/astral-validation-state';
import { ASTRAL_MAX_DATE_TIME, astralSupportedYearValidator } from '../astral-components/astral-date-validation';
import { InputControlBase } from '../forms/input-control.base';
import { AstralTemporalInputDirective } from '../astral-components/astral-temporal-input/astral-temporal-input.directive';

/*
Uses datetime-local so support for FF and safari isn't complete see
https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input/datetime-local#browser_compatibility
*/

@Component({
    selector: 'app-date-time-picker',
    template: `
    <astral-field-shell
      [control]="control"
      [forId]="inputId"
      [helpText]="helpText"
      [label]="control.caption"
      [readonly]="control.isReadonly"
      [disabled]="control.disabled"
      [required]="control.required"
      [errorMessage]="errorMessage"
      [invalid]="invalid"
    >
      <input
        #dateinput="ngModel"
        astralFieldAccessibility
        astralTemporalInput="datetime-local"
        [astralFieldControl]="control"
        [astralFieldHelpText]="helpText"
        [astralFieldInputId]="inputId"
        [astralFieldInvalid]="invalid"
        [astralFieldRequired]="control.required"
        class="astral-date-time-picker__control"
        type="datetime-local"
        [attr.max]="maxDateTime"
        [attr.id]="inputId"
        [attr.name]="inputId"
        [ngModel]="internalValue"
        [ngModelOptions]="{ standalone: true }"
        [readonly]="control.isReadonly ? true : null"
        [disabled]="control.disabled ? true : null"
        [required]="control.required ? true : null"
        (ngModelChange)="internalValueChanged($event)"
        (astralTemporalInputCommit)="internalValueChanged($event)"
        (blur)="markTouch()"
      />
    </astral-field-shell>
    `,
    styles: [
        `
          :host {
            display: block;
            max-width: 100%;
            min-width: 0;
          }

          .astral-date-time-picker__control {
            background-color: var(--color-white, #ffffff);
            border: 1px solid var(--astral-field-border-color);
            border-radius: var(--astral-field-border-radius);
            box-sizing: border-box;
            color: inherit;
            min-height: var(--astral-form-control-height, 44px);
            min-width: 0;
            outline: none;
            padding: var(--spacing-xs, 8px);
            transition: border-color 120ms ease, box-shadow 120ms ease;
            width: 100%;
          }

          .astral-date-time-picker__control:focus-visible:not(:read-only):not(:disabled) {
            border-color: var(--astral-field-focus-color);
            box-shadow: var(--astral-field-focus-ring);
          }

          .astral-date-time-picker__control:disabled {
            background-color: var(--astral-field-disabled-background);
            border-color: var(--astral-field-disabled-border-color);
            color: var(--color-seared-gray, #495057);
            cursor: not-allowed;
          }

          .astral-date-time-picker__control:read-only:not(:disabled) {
            background-color: transparent;
            border-inline: 0;
            border-radius: 0;
            border-top: 0;
            border-bottom: 1px solid var(--color-million-gray, #6c757d);
          }

          .astral-date-time-picker__control:read-only:focus-visible {
            border-bottom-color: var(--astral-field-readonly-focus-color);
            box-shadow: var(--astral-field-readonly-focus-shadow);
          }
         `,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DateTimePickerComponent extends InputControlBase implements OnInit {
  private static nextId = 0;
  protected readonly inputId = `astral-date-time-picker-${++DateTimePickerComponent.nextId}`;
  protected internalValue: string;
  protected readonly maxDateTime = ASTRAL_MAX_DATE_TIME;

  @ViewChild('dateinput') dateinput: NgModel;

  protected get invalid(): boolean {
    return !!this.errorMessage && shouldShowAstralValidationError(this.control);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.control.addValidators(astralSupportedYearValidator);
    this.control.updateValueAndValidity({ emitEvent: false });

    this.subscription.add(
      this.control.valueChanges.pipe(startWith(this.control.value)).subscribe({
        next: controlValue => {
          this.internalValue = controlValue ? formatDate(new Date(controlValue), 'yyyy-MM-ddTHH:mm', 'en-US') : null;
        },
      })
    );

    this.subscription.add(
      this.control.touchStateChanged.subscribe({
        next: () => {
          this.dateinput.control.markAsTouched();
        },
      })
    );
  }

  override ngOnDestroy(): void {
    this.control?.removeValidators(astralSupportedYearValidator);
    this.control?.updateValueAndValidity({ emitEvent: false });
    super.ngOnDestroy();
  }

  protected internalValueChanged(change: string): void {
    this.control.setValue(change ? new Date(change).toISOString() : null);
    this.control.markAsDirty();
  }

  protected markTouch() {
    this.control.markAsTouched();
  }
}
